import { graphql } from '../../gql';
import { useApiMutation } from '../../query/hooks';
import { FC } from 'react';
import { useIntl } from 'react-intl';
import { ModalActions } from '../../modal/components/ModalActions';
import { ModalContent } from '../../modal/components/ModalContent';
import { useModalContext } from '../../modal/ModalContext';
import { z } from 'zod';
import { Box } from '@mui/system';
import { CircularProgress, DialogContent } from '@mui/material';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { useAuthSignedIn } from '../../auth/useAuth';
import { useSnackbar } from '../../store/snackbar';

const updateMemberMutation = graphql(`
	mutation deleteProfilePhoto_updateMember($memberId: UUID!) {
		updateMember(by: { id: $memberId }, data: { avatar: { disconnect: true } }) {
			ok
			errorMessage
		}
	}
`);

export const deleteProfilePhotoModalSchema = z.object({});

export const DeleteProfilePhotoModal: FC<z.infer<typeof deleteProfilePhotoModalSchema>> = () => {
	const intl = useIntl();
	const { me } = useAuthSignedIn();
	const { openSnackbar } = useSnackbar();

	const { mutate: deleteAvatar, isPending: isDeleteAvatarPending } = useApiMutation(updateMemberMutation, {
		onSuccess: (result) => {
			mutationResponseHandler({
				result: Boolean(result.updateMember.ok),
				toastMessages: {
					error: intl.formatMessage({ id: 'common.error.default' }),
				},
				errorMessage: result.updateMember.errorMessage,
				openSnackbar,
			});
		},
	});
	const { closeModal } = useModalContext();

	// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
	if (me == null) {
		return (
			<DialogContent sx={{ p: 4 }}>
				<Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
					<CircularProgress />
				</Box>
			</DialogContent>
		);
	}

	return (
		<>
			<ModalContent
				isDelete
				title={intl.formatMessage({ id: 'form.profile.delete-avatar.title' })}
				description={intl.formatMessage({ id: 'form.profile.delete-avatar.description' })}
			/>
			<ModalActions
				isDelete
				handleCancelButtonClick={closeModal}
				handlePrimaryButtonClick={() => {
					deleteAvatar(
						{ memberId: me.id },
						{
							onSettled: () => {
								closeModal();
							},
						},
					);
				}}
				cancelButtonText={intl.formatMessage({ id: 'common.cancel' })}
				primaryButtonText={
					isDeleteAvatarPending ? intl.formatMessage({ id: 'common.loading' }) : intl.formatMessage({ id: 'common.delete' })
				}
				disablePrimaryButton={isDeleteAvatarPending}
			/>
		</>
	);
};
